<script lang="ts" context="module">
	import {
		Observable,
		Subject,
		Subscription,
		debounceTime,
		distinctUntilChanged,
		map,
		share,
	} from "rxjs";
	import { widthFromResizeObserver } from "$lib/util/obs";

	let instanceCount = 0;
	export const getNextInstanceCount = () => instanceCount++;
	export type CarouselType =
		| 'brands' // this works believe it or not :)
		| 'swimlane'
		| 'banners small'
		| 'banners large'
		| 'banners deluxe'
		| 'filters primary'
		| 'filters secondary'
		| 'aisle'
		| 'pills'
		| 'seo-brands';

	let flickityPromise: Promise<void | typeof Flickity>;
	let fadePromise: Promise<void | typeof Flickity>;

	if (browser) {
		flickityPromise = import("flickity")
			.then((lib) => lib.default)
			.catch((er) => console.log("Flickity carousel error", er));

		// @ts-ignore
		fadePromise = import("flickity-fade")
			.then((lib) => lib.default)
			.catch((er) => console.log("Flickity fade carousel error", er));
	}
</script>

<script lang="ts" generics="T, R">
	import type { Cache } from "$lib/serv/core/cache";
	import type Flickity from 'flickity';

	import { browser } from '$app/environment';
	import { fade, fly } from 'svelte/transition';
	import { onDestroy } from 'svelte';

	import 'flickity/css/flickity.css';
	import 'flickity-fade/flickity-fade.css';
	import '../../../scss/component/carousel.scss';

	interface Options extends Flickity.Options { fade?: boolean }

	export let cache: Cache<T> = { loading: true };
	export let type: CarouselType;
	export let key: (payload: T) => R[] | undefined = (payload) => Array.isArray(payload) ? payload as R[] : [];
	/**
	 * For dynamic carousels (e.g. grocery LI saved items swimlane), we use this in a keyed each
	 * block to help Svelte manage carousel item removals correctly
	 * */
	export let uniqueKey: ((item: R) => string|number) | undefined = undefined;
	export let options: Options = {};
	export let id = '';
	export let grocPDPcarousel = false; // Dan, can we discuss an alternative way to handle this?

	$: isFoodCarousel = ['swimlane', 'banners small', 'banners large', 'banners deluxe'].includes(type);
	$: isGrocCarousel = ['filters primary', 'filters secondary', 'aisle'].includes(type);

	const resize$ = new Subject<void>();

	$: if (cache?.payload) reload(key(cache.payload)?.length ?? -1);

	let slideIndex = 0;
	let prevItems: R[] | undefined = undefined; // this is an attempt to keep the filter heading until new data arrives to reduce flicker

	const resizeSub = resize$.pipe(debounceTime(50)).subscribe(() => {
		if (flickity) {
			flickity.resize();
			slideIndex = flickity.selectedIndex;
		}
	});

	export const resize = () => {
		resize$.next();
	};

	interface $$Slots {
		title: {};
		default: {
			item: R;
			index: number;
		};
  }

	let flickity: Flickity;
	let element: HTMLElement;
	let hidden: boolean = true;

	const noAnimationAfter: number = 1024; // larger than

	const defaultOptions: Options = {
		cellAlign: 'left',
		prevNextButtons: true,
		draggable: true,
		wrapAround: false,
		groupCells: true,
		pageDots: false,
		contain: true,
		lazyLoad: true,
		arrowShape: '',
		on: {
			ready: () => hidden = false,
			change: (index) => slideIndex = index || 0,
		}
	};

	const smallScreenOptions: Options = {
		...defaultOptions,
		fade: false,
		...options
	};
	const largeScreenOptions: Options = {
		...defaultOptions,
		draggable: false,
		selectedAttraction: 0,
		friction: 1,
		fade: true,
		...options
	};

	const count = getNextInstanceCount();

	let width$: Observable<number>;
	let widthSub: Subscription;

	let innerWidth: number = browser ? window.innerWidth : 0;
	let prevInnerWidth: number = innerWidth;

	function observeResize(el: HTMLElement) {
		if (!width$)
			width$ = widthFromResizeObserver(el).pipe(
				distinctUntilChanged(),
				debounceTime(50),
				map((_) => prevInnerWidth),
				share()
			);
	}

	function init(el: HTMLElement, [items]: [R[]]) {
		observeResize(el);
		element = el;

		widthSub = width$.subscribe((prev) => {
			if (
				(prev <= noAnimationAfter && innerWidth > noAnimationAfter) || // screen expanded past breakpoint
				(prev > noAnimationAfter && innerWidth <= noAnimationAfter) // screen contracted past breakpoint
			) {
				if (flickity) {
					flickity.destroy();
					initFlickity();
				}
			} else {
				if (flickity) flickity.resize();
			}
			prevInnerWidth = innerWidth;
		});

		initFlickity();

		return {
			// When this component receives a new list of items to show, we need to let flickity know to
			// re-run its rendering logic.  Without this, it behaves inconsistently -- sometimes it would
			// just render an empty carousel with the items chilling outside :/
			update([items]: [R[]]) {
				if (flickity) {
					flickity.destroy();
					initFlickity();
				}
			},

			// Juuust to be safe: when the carousel div is removed from the DOM, let's also destroy the
			// flickity representation of it so there's no chance of it being re-used :?
			destroy() {
				flickity?.destroy();
			},
		}
	}

	async function initFlickity() {
		await new Promise((resolve) => setTimeout(resolve, 2000));

		await Promise.all([flickityPromise, fadePromise]).then(([carousel, f]) => {
			// @ts-ignore
			flickity = new carousel(element, {
				...(innerWidth <= noAnimationAfter ? smallScreenOptions : largeScreenOptions),
				prevNextButtons: false, // added manually in template
			});
		})
	};

	/** Stores the length of the previous payload. */
	let payloadLength = -1;

	/**
	 * Tear down and re-initialise Flickity when the length of the payload changes (if the payload
	 * was previously and is currently defined).  Necessary for carousels that shrink and grow after
	 * initialisation, e.g. grocery LI saved items swimlane, which is affected by products being un/saved.
	 * @param newPayloadLength The length of the new payload, to compare to the payload's previous length
	 */
	function reload(newPayloadLength: number) {
		if (flickity && length >= 0 && newPayloadLength >= 0
			&& newPayloadLength !== payloadLength) {
			flickity.destroy();
			initFlickity();
		}

		payloadLength = newPayloadLength;
	}
	
	onDestroy(() => {
		resizeSub.unsubscribe();
		widthSub?.unsubscribe();
	});

</script>

<svelte:window bind:innerWidth />

<div class="m-v-l relative" data-testid={id}>
	{#if cache}
		{#if !prevItems && cache.loading}
			{#if $$slots.title}
				<div class="title loading">
					<div class="headline-m">&nbsp;</div>
				</div>
			{/if}
			<div class="carousel-container">
				<div class="carousel loading {type}">
					<div class="flickity-viewport">
						{#each Array(9) as _}
							<div class="item" in:fly|global={{ x: 100, delay: 50 }}>
								<div class="image" />
								<div class="text" />
							</div>
						{/each}
					</div>
				</div>
			</div>
		{:else}
			{@const { error, payload } = cache ?? {}}
			{@const items = prevItems = payload ? key(payload) : prevItems}
			{#if cache.loading}
				<!-- show smaller inline loader? -->
			{/if}
			{#if error}
				<div class="message error body-m book">
					<p class="body-m book">
						{error?.friendly_message ?? error ?? "Unable to load grid."}
					</p>
				</div>
			{:else if items?.length}
				<div class="title flex small-gap justify-end {grocPDPcarousel ? 'align-end' : ''}" class:absolute={!$$slots.title}>
					{#if $$slots.title}
						<div class="fill-space">
							<slot name="title" />
						</div>
					{/if}
					{#if largeScreenOptions.prevNextButtons}
						<div class="desktop-only button-group align-end" class:hidden>
							<button on:click={() => flickity.previous()} class="button-circle" disabled={slideIndex === 0}><span class="icon-chevron-left-28"></span></button>
							<button on:click={() => flickity.next()} class="button-circle" disabled={slideIndex >= flickity?.slides.length - 1}><span class="icon-chevron-right-28"></span></button>
						</div>
					{/if}
				</div>
				<div class="carousel-container">
					<div use:init={[items]} class="carousel {type}" in:fade|global class:food-carousel={isFoodCarousel} class:groc-carousel={isGrocCarousel}>
						{#each items as item, index (uniqueKey ? uniqueKey(item) : index)}
							<slot {item} {index} />
						{/each}
					</div>
				</div>
			{/if}
		{/if}
	{/if}
</div>


